import { useSEO } from "@/hooks/useSEO";
// Amplitude tracking removed
import { useEffect, useRef, useState } from "react";
import { Instagram, Youtube } from "lucide-react";
import profileImage from "@/assets/profile.jpg";
import reel1 from "@/assets/reel-1.jpg";
import reel2 from "@/assets/reel-2.jpg";
import reel3 from "@/assets/reel-3.jpg";
import { BRAND_HANDLE, BRAND_PERSON_NAME } from "@/lib/brandConfig";

const socialLinks = [
  { icon: Instagram, label: "Instagram", href: `https://instagram.com/${BRAND_HANDLE}` },
  { icon: Youtube, label: "YouTube", href: `https://www.youtube.com/@${BRAND_HANDLE}` },
];

const photos = [
  { id: 1, src: reel1, alt: `${BRAND_PERSON_NAME} Foto 1` },
  { id: 2, src: reel2, alt: `${BRAND_PERSON_NAME} Foto 2` },
  { id: 3, src: reel3, alt: `${BRAND_PERSON_NAME} Foto 3` },
  { id: 4, src: reel1, alt: `${BRAND_PERSON_NAME} Foto 4` },
  { id: 5, src: reel2, alt: `${BRAND_PERSON_NAME} Foto 5` },
  { id: 6, src: reel3, alt: `${BRAND_PERSON_NAME} Foto 6` },
];

const Fotos = () => {
  const [selectedPhoto, setSelectedPhoto] = useState<typeof photos[0] | null>(null);

  useSEO("fotos");

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-md mx-auto px-5 py-12 md:py-16">
        <header className="text-center mb-10 animate-fade-in">
          <div className="relative inline-block mb-8">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-border via-muted to-border opacity-60" />
            <div className="relative w-28 h-28 rounded-full overflow-hidden ring-2 ring-background shadow-lg">
              <img
                src={profileImage}
                alt={`${BRAND_PERSON_NAME} - Photo gallery profile picture`}
                loading="lazy"
                width={112}
                height={112}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-background/10 to-transparent pointer-events-none" />
          </div>

          <h1 className="text-3xl md:text-4xl font-display font-medium text-foreground tracking-wide mb-3 italic">
            Fotos
          </h1>
          <div className="flex items-center justify-center gap-2 mb-5">
            <span className="w-6 h-px bg-border" />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
              Galería de {BRAND_PERSON_NAME}
            </p>
            <span className="w-6 h-px bg-border" />
          </div>
        </header>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4 text-center">
            <p className="text-sm leading-relaxed">
              Galería de <strong>fotos</strong> de {BRAND_PERSON_NAME}. Imágenes de lifestyle, viajes, 
              belleza, bienestar y momentos especiales capturados para compartir con mi comunidad.
            </p>
            <p className="text-sm leading-relaxed">
              Encuentra más fotos diarias en mis redes sociales: Instagram, TikTok y YouTube.
            </p>
          </div>
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.2s" }}>
          <h2 className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-6 font-medium">
            Galería
          </h2>
          <div className="grid grid-cols-3 gap-[2px]">
            {photos.map((photo, index) => (
              <button
                type="button"
                key={photo.id}
                onClick={() => {
                  // Gallery item tracking removed
                  // Lightbox tracking removed
                  lightboxOpenTimeRef.current = Date.now();
                  setSelectedPhoto(photo);
                }}
                aria-label={`View ${photo.alt}`}
                className="group relative aspect-[3/4] overflow-hidden bg-muted focus:outline-none focus:ring-2 focus:ring-foreground/20"
              >
                <img
                  src={photo.src}
                  alt={`${photo.alt} - Photo from {BRAND_PERSON_NAME} gallery`}
                  loading="lazy"
                  width={300}
                  height={400}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </button>
            ))}
          </div>
        </section>

        <section className="mb-10 animate-fade-in" style={{ animationDelay: "0.3s" }}>
          <div className="flex justify-center gap-6">
            {socialLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-300"
                aria-label={link.label}
              >
                <link.icon className="w-4 h-4" strokeWidth={1.5} />
              </a>
            ))}
          </div>
        </section>

        <div className="text-center animate-fade-in" style={{ animationDelay: "0.4s" }}>
          <a
            href="/"
            className="inline-block text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Volver al inicio
          </a>
        </div>

        <footer className="mt-14 text-center animate-fade-in" style={{ animationDelay: "0.5s" }}>
          <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60">
            {`@${BRAND_HANDLE}`}
          </p>
        </footer>
      </main>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-foreground/95 flex items-center justify-center p-4"
          onClick={() => {
            if (selectedPhoto && lightboxOpenTimeRef.current) {
              const timeSpent = Math.round((Date.now() - lightboxOpenTimeRef.current) / 1000);
              // trackLightboxClose removed("photo", selectedPhoto.id, selectedPhoto.alt, timeSpent);
              lightboxOpenTimeRef.current = null;
            }
            setSelectedPhoto(null);
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (selectedPhoto && lightboxOpenTimeRef.current) {
                const timeSpent = Math.round((Date.now() - lightboxOpenTimeRef.current) / 1000);
                // Lightbox close tracking removed
                lightboxOpenTimeRef.current = null;
              }
              setSelectedPhoto(null);
            }}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-background/80 hover:text-background transition-colors z-10 focus:outline-none focus:ring-2 focus:ring-background/50 rounded"
            aria-label="Cerrar"
          >
            <span className="text-2xl">×</span>
          </button>
          
          <div 
            className="relative max-w-sm w-full max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
              <img
                src={selectedPhoto.src}
                alt={`${selectedPhoto.alt} - Full size photo from {BRAND_PERSON_NAME} gallery`}
                className="w-full h-auto max-h-[85vh] object-contain rounded"
              />
          </div>
        </div>
      )}
    </div>
  );
};

export default Fotos;


